import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { CheckCircle, XCircle, Eye, Clock, Buildings, User } from "@phosphor-icons/react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import api, { API, formatIDR, formatDate, formatApiError } from "../lib/api";

const STATUS_META = {
  pending: { label: "MENUNGGU", cls: "bg-amber-100 text-amber-800 border-amber-300" },
  approved: { label: "DISETUJUI", cls: "bg-emerald-100 text-emerald-800 border-emerald-300" },
  rejected: { label: "DITOLAK", cls: "bg-rose-100 text-rose-800 border-rose-300" },
};

export default function ApprovalPembayaran() {
  const [items, setItems] = useState([]);
  const [lembagas, setLembagas] = useState([]);
  const [status, setStatus] = useState("pending");
  const [lembagaFilter, setLembagaFilter] = useState("all");
  const [loading, setLoading] = useState(false);
  const [previewItem, setPreviewItem] = useState(null);
  const [rejectItem, setRejectItem] = useState(null);
  const [rejectReason, setRejectReason] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const q = new URLSearchParams();
      if (status !== "all") q.set("status", status);
      if (lembagaFilter !== "all") q.set("lembaga_id", lembagaFilter);
      const [d, l] = await Promise.all([
        api.get(`/pengajuan-pembayaran?${q}`),
        api.get("/lembaga").catch(() => ({ data: [] })),
      ]);
      setItems(d.data); setLembagas(l.data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [status, lembagaFilter]);

  const approve = async (p) => {
    if (!confirm(`Setujui pembayaran ${p.siswa_name} sebesar ${formatIDR(p.amount)}?\n\nAkan otomatis tercatat sebagai kwitansi dan mengurangi tagihan.`)) return;
    try {
      const { data } = await api.post(`/pengajuan-pembayaran/${p.id}/approve`);
      toast.success(`Disetujui — kwitansi ${data.receipt_no}`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const openReject = (p) => { setRejectItem(p); setRejectReason(""); };
  const doReject = async () => {
    if (!rejectReason.trim()) { toast.error("Alasan penolakan wajib diisi"); return; }
    try {
      await api.post(`/pengajuan-pembayaran/${rejectItem.id}/reject`, { reason: rejectReason });
      toast.success("Pengajuan ditolak");
      setRejectItem(null); setRejectReason(""); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const pendingCount = items.filter(i => i.status === "pending").length;

  return (
    <div data-testid="approval-page">
      <div className="mb-6 flex flex-col sm:flex-row sm:items-end justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-[#0B8D95] font-semibold mb-1">Keuangan</div>
          <h1 className="font-display text-3xl font-black text-gray-900">Approval Pembayaran</h1>
          <p className="text-gray-600 text-sm mt-1">Setujui/tolak pengajuan pembayaran dari wali murid. Setelah disetujui, kwitansi otomatis dibuat dan tagihan berkurang.</p>
        </div>
        {pendingCount > 0 && (
          <div className="inline-flex items-center gap-2 bg-amber-50 border border-amber-300 text-amber-800 px-4 py-2 rounded-lg">
            <Clock size={18} weight="duotone" />
            <span className="text-sm font-bold">{pendingCount} menunggu review</span>
          </div>
        )}
      </div>

      <div className="bg-white border border-gray-200 rounded-md overflow-hidden">
        <div className="p-4 border-b border-gray-200 flex flex-wrap gap-3 items-end">
          <div><Label className="text-xs">Status</Label>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger className="w-40 bg-white" data-testid="approval-status-filter"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="pending">Menunggu</SelectItem>
                <SelectItem value="approved">Disetujui</SelectItem>
                <SelectItem value="rejected">Ditolak</SelectItem>
                <SelectItem value="all">Semua</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div><Label className="text-xs">Lembaga</Label>
            <Select value={lembagaFilter} onValueChange={setLembagaFilter}>
              <SelectTrigger className="w-56 bg-white" data-testid="approval-lembaga-filter"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white max-h-72">
                <SelectItem value="all">Semua Lembaga</SelectItem>
                {lembagas.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="text-xs text-gray-500 font-mono ml-auto self-center">{items.length} pengajuan</div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-left text-xs uppercase tracking-wider text-gray-500">
              <tr>
                <th className="px-3 py-3 font-semibold">Tanggal</th>
                <th className="px-3 py-3 font-semibold">Siswa</th>
                <th className="px-3 py-3 font-semibold">Lembaga</th>
                <th className="px-3 py-3 font-semibold">Nomen</th>
                <th className="px-3 py-3 font-semibold text-right">Jumlah</th>
                <th className="px-3 py-3 font-semibold">Rekening</th>
                <th className="px-3 py-3 font-semibold text-center">Status</th>
                <th className="px-3 py-3 font-semibold text-right">Aksi</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={8} className="p-10 text-center text-gray-400">Memuat...</td></tr>}
              {!loading && items.length === 0 && <tr><td colSpan={8} className="p-10 text-center text-gray-400">Belum ada pengajuan</td></tr>}
              {items.map((p) => (
                <tr key={p.id} className="border-t border-gray-100 hover:bg-gray-50" data-testid={`approval-row-${p.id}`}>
                  <td className="px-3 py-2.5 font-mono text-xs">{formatDate(p.payment_date || p.submitted_at)}</td>
                  <td className="px-3 py-2.5"><div className="font-semibold">{p.siswa_name || "-"}</div><div className="text-xs text-gray-500">NIS {p.siswa_nis || "-"} · {p.class_name || "-"}</div></td>
                  <td className="px-3 py-2.5 text-xs">{p.lembaga_name || "-"}</td>
                  <td className="px-3 py-2.5"><span className="inline-block px-2 py-0.5 rounded bg-[#CCFBF1] text-[#0B8D95] text-xs font-mono font-semibold">{p.nomenklatur_code}</span></td>
                  <td className="px-3 py-2.5 text-right font-mono font-bold text-[#059669]">{formatIDR(p.amount)}</td>
                  <td className="px-3 py-2.5 text-xs">{p.rekening_label || "-"}</td>
                  <td className="px-3 py-2.5 text-center">
                    <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-black border ${STATUS_META[p.status]?.cls || ""}`}>{STATUS_META[p.status]?.label || p.status?.toUpperCase()}</span>
                    {p.status === "rejected" && p.rejection_reason && <div className="text-[10px] text-rose-600 mt-1 italic">"{p.rejection_reason}"</div>}
                  </td>
                  <td className="px-3 py-2.5 text-right whitespace-nowrap">
                    <button onClick={() => setPreviewItem(p)} className="p-1.5 hover:bg-blue-50 rounded text-blue-600" title="Lihat bukti" data-testid={`approval-view-${p.id}`}><Eye size={14} /></button>
                    {p.status === "pending" && (
                      <>
                        <button onClick={() => approve(p)} className="p-1.5 hover:bg-emerald-50 rounded text-emerald-600" title="Setujui" data-testid={`approval-approve-${p.id}`}><CheckCircle size={14} weight="fill" /></button>
                        <button onClick={() => openReject(p)} className="p-1.5 hover:bg-rose-50 rounded text-rose-600" title="Tolak" data-testid={`approval-reject-${p.id}`}><XCircle size={14} weight="fill" /></button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Preview bukti */}
      <Dialog open={!!previewItem} onOpenChange={(v) => !v && setPreviewItem(null)}>
        <DialogContent className="bg-white max-w-2xl" data-testid="approval-preview-dialog">
          <DialogHeader><DialogTitle>Detail Pengajuan</DialogTitle></DialogHeader>
          {previewItem && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><Label className="text-xs">Siswa</Label><div className="font-semibold">{previewItem.siswa_name} · NIS {previewItem.siswa_nis}</div></div>
                <div><Label className="text-xs">Lembaga</Label><div>{previewItem.lembaga_name || "-"}</div></div>
                <div><Label className="text-xs">Nomenklatur</Label><div>{previewItem.nomenklatur_code} · {previewItem.nomenklatur_name}</div></div>
                <div><Label className="text-xs">Jumlah</Label><div className="font-mono font-bold text-[#059669]">{formatIDR(previewItem.amount)}</div></div>
                <div><Label className="text-xs">Tanggal Bayar</Label><div>{formatDate(previewItem.payment_date)}</div></div>
                <div><Label className="text-xs">Rekening Tujuan</Label><div>{previewItem.rekening_label || "-"}</div></div>
                {previewItem.catatan && <div className="col-span-2"><Label className="text-xs">Catatan Wali</Label><div className="italic text-gray-600">"{previewItem.catatan}"</div></div>}
              </div>
              <div>
                <Label className="text-xs">Bukti Transfer</Label>
                {previewItem.bukti_path?.toLowerCase().endsWith(".pdf") ? (
                  <a href={`${API}/files/${previewItem.bukti_path}`} target="_blank" rel="noopener noreferrer" className="block mt-1 p-4 bg-blue-50 text-blue-700 rounded text-center font-semibold hover:bg-blue-100">📄 Buka Bukti (PDF)</a>
                ) : (
                  <img src={`${API}/files/${previewItem.bukti_path}`} alt="Bukti" className="mt-1 max-h-96 mx-auto border border-gray-200 rounded" />
                )}
              </div>
            </div>
          )}
          <DialogFooter>
            {previewItem?.status === "pending" && (
              <>
                <Button variant="outline" onClick={() => { openReject(previewItem); setPreviewItem(null); }} className="text-rose-600 border-rose-300 hover:bg-rose-50"><XCircle size={14} className="mr-1.5" />Tolak</Button>
                <Button onClick={() => { approve(previewItem); setPreviewItem(null); }} className="bg-emerald-600 hover:bg-emerald-700 text-white"><CheckCircle size={14} className="mr-1.5" />Setujui</Button>
              </>
            )}
            <Button variant="outline" onClick={() => setPreviewItem(null)}>Tutup</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject dialog */}
      <Dialog open={!!rejectItem} onOpenChange={(v) => !v && setRejectItem(null)}>
        <DialogContent className="bg-white" data-testid="approval-reject-dialog">
          <DialogHeader><DialogTitle>Tolak Pengajuan</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-gray-600">Pengajuan dari <b>{rejectItem?.siswa_name}</b> sebesar <b>{formatIDR(rejectItem?.amount)}</b></p>
            <div><Label>Alasan Penolakan *</Label><Textarea rows={3} value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} placeholder="Contoh: Bukti transfer tidak jelas / nominal tidak sesuai" data-testid="reject-reason-input" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectItem(null)}>Batal</Button>
            <Button onClick={doReject} className="bg-rose-600 hover:bg-rose-700 text-white" data-testid="reject-submit-btn">Tolak Pengajuan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
